/**
 * JSON tree preview — collapsible, hand-rolled (no extra dependency).
 * Falls back to a syntax-highlighted code view on parse errors.
 */

import { ChevronRight } from "lucide-react";
import { useT } from "@magic-md/i18n/react";
import { useMemo, useState } from "react";

interface Props {
	content: string;
}

type JsonValue =
	| null
	| boolean
	| number
	| string
	| JsonValue[]
	| { [k: string]: JsonValue };

function TreeNode({
	keyName,
	value,
	depth,
	defaultOpen,
}: {
	keyName?: string;
	value: JsonValue;
	depth: number;
	defaultOpen: boolean;
}) {
	const [open, setOpen] = useState(defaultOpen);
	const isObject = value !== null && typeof value === "object";
	const isArray = Array.isArray(value);
	const entries = isObject
		? isArray
			? (value as JsonValue[]).map((v, i) => [String(i), v] as const)
			: Object.entries(value as Record<string, JsonValue>)
		: [];

	const renderPrimitive = (v: JsonValue) => {
		if (v === null) return <span className="text-muted-foreground">null</span>;
		if (typeof v === "boolean")
			return <span className="text-blue-600 dark:text-blue-400">{String(v)}</span>;
		if (typeof v === "number")
			return <span className="text-amber-600 dark:text-amber-400">{v}</span>;
		return <span className="text-emerald-600 dark:text-emerald-400">"{String(v)}"</span>;
	};

	if (!isObject) {
		return (
			<div
				className="flex items-start gap-1 text-[12px] font-mono leading-5"
				style={{ paddingLeft: depth * 12 }}
			>
				{keyName !== undefined ? (
					<span className="text-foreground/80">"{keyName}":</span>
				) : null}
				{renderPrimitive(value)}
			</div>
		);
	}

	const summary = isArray
		? `Array(${entries.length})`
		: `Object(${entries.length})`;

	return (
		<div className="text-[12px] font-mono leading-5">
			<button
				type="button"
				onClick={() => setOpen((v) => !v)}
				className="flex items-center gap-1 hover:bg-muted/40 rounded px-0.5 cursor-pointer w-full text-left"
				style={{ paddingLeft: depth * 12 }}
			>
				<ChevronRight
					size={12}
					className={`transition-transform shrink-0 ${open ? "rotate-90" : ""}`}
				/>
				{keyName !== undefined ? (
					<span className="text-foreground/80">"{keyName}":</span>
				) : null}
				<span className="text-muted-foreground">{summary}</span>
			</button>
			{open
				? entries.map(([k, v]) => (
						<TreeNode
							key={k}
							keyName={k}
							value={v}
							depth={depth + 1}
							defaultOpen={depth < 1}
						/>
					))
				: null}
		</div>
	);
}

export function JsonTreePreview({ content }: Props) {
	const t = useT("editor");
	const parsed = useMemo<{ ok: true; value: JsonValue } | { ok: false; error: string }>(() => {
		try {
			return { ok: true, value: JSON.parse(content) as JsonValue };
		} catch (err) {
			return { ok: false, error: err instanceof Error ? err.message : String(err) };
		}
	}, [content]);

	if (!parsed.ok) {
		return (
			<div className="space-y-2">
				<div className="text-[11px] text-red-600 dark:text-red-400 bg-red-500/10 rounded px-2 py-1.5">
					{t("preview.jsonParseFailed", { message: parsed.error })}
				</div>
				<pre className="text-[11px] font-mono whitespace-pre-wrap break-all bg-muted/30 rounded p-3">
					{content}
				</pre>
			</div>
		);
	}

	return (
		<div className="bg-muted/20 rounded p-2">
			<TreeNode value={parsed.value} depth={0} defaultOpen={true} />
		</div>
	);
}
